import { faBolt, faCaretDown, faCircle, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useCallback, useEffect, useState } from "react";
import { Badge, Button, Col, ListGroup, Modal, Row } from "react-bootstrap";
import { useNavigate } from 'react-router-dom';
import uPlot from "uplot";
import { CHARTED_VITAL_SIGNS_BY_MRN, DIAGNOSES_BY_MRN, Diagnosis, PATIENT_VISITS_BY_MRN_WITH_SUMMARY, PatientSummary, callQuery } from "../../amplify/appsync/query";
import { PatientVisit } from '../../amplify/appsync/search-patients';
import UplotReact from '../dashboard/UplotReact';
import { MultiSelectMenu } from "../filters/MultiSelectMenu";
import { getVisitOpts, getVisitSummaryOpts } from "./opts";
import { PatientVisitSelectorItem } from "./PatientVisitSelectorItem";
import { VisitSelectorAssistant } from "./VisitSelectorAssistant";
import PersonalIdentifier from "../common/PersonalIdentifier";

export const ENCOUNTER_TYPES = ["Emergency", "Hospital Encounter", "Outpatient", "Ambulatory", "Other"]


/**
 * Shows and allows users to select previous patient visits
 */
export const PatientVisitSelector = React.memo((props: {
    user: any,
    showPatientVisitSelectorModal: boolean, 
    setShowPatientVisitSelectorModal: any,
    patientVisit: PatientVisit,
    width: number,
    ws: string,
}) => {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [showAssistant, setShowAssistant] = useState<any>(false);
    const [filterShow, setFilterShow] = useState<boolean>(false);
    const [filterOptions, setFilterOptions] = useState<any>({
        "encounter": ["Emergency", "Hospital Encounter", "Ambulatory"], // default state
    });
    const [highlightedPatientVisit, setHighlightedPatientVisit] = useState<any>(null);
    const [relatedPatientVisits, setRelatedPatientVisits] = useState<any>([]);
    const [visitSummaryPlotData, setVisitSummaryPlotData] = useState<any>([[], [], [], [], []]);
    const [vitalSignToPlotData, setVitalSignToPlotData] = useState<any>([]);
    const [visitSummaryOpts, setVisitSummaryOpts] = useState<any>({});
    const [multiVisitOpts, setMultiVisitOpts] = useState<any>({});
    const [cursPos, setCursPos] = useState<any>({
        left: -10,
        top: -10,
    });
    const [expand, setExpand] = useState<any>({});
    const [diagnoses, setDiagnoses] = useState<any>([]);
    const [csnToNotes, setCsnToNotes] = useState<any>({});

    const convertToPlotData = (relatedPatientVisits: PatientVisit[], queryData: any) => {
        const vitalSignToPlotData: any = {
            "csn": [],
            "numeric_time": [],
            "hr": [],
            "rr": [], 
            "spo2": [], 
            "nbps": [], 
            "nbpd": [], 
            "temp": [],
            "encounter_types": [],
        };
        const ambulatoryVitalSignToPlotData: any = {
            "csn": [],
            "numeric_time": [],
            "hr": [],
            "rr": [], 
            "spo2": [], 
            "nbps": [], 
            "nbpd": [], 
            "temp": [],
            "encounter_types": [],
        };

        const triageVitals = relatedPatientVisits.map(pt => {
            return [
                "",
                `${pt.csn}-t`,
                pt.triage_hr === null || isNaN(pt.triage_hr) ? null : pt.triage_hr,
                pt.triage_rr === null || isNaN(pt.triage_rr) ? null : pt.triage_rr,
                pt.triage_spo2 === null || isNaN(pt.triage_spo2) ? null : pt.triage_spo2,
                pt.triage_sbp === null || isNaN(pt.triage_sbp) ? null : pt.triage_sbp,
                pt.triage_dbp === null || isNaN(pt.triage_dbp) ? null : pt.triage_dbp,
                pt.triage_temp === null || isNaN(pt.triage_temp) ? null : pt.triage_temp,
                pt.arrival_time,
                pt.encounter_types,
            ]
        })

        const interweavedData = [...triageVitals, ...queryData];
        interweavedData.sort((a, b) => a[8].localeCompare(b[8]));

        for (let row of interweavedData) {
            let includeRow = false;
            for (let encounterType of filterOptions["encounter"]) {
                if (row[9] && row[9].includes(encounterType)) {
                    includeRow = true;
                }
            }
            if (filterOptions["encounter"].includes("Other") && row[9] && !(row[9].includes("Emergency") || row[9].includes("Hospital Encounter") || row[9].includes("Outpatient") || row[9].includes("Ambulatory"))) {
                includeRow = true;
            }
            if (!row[9]) {
                includeRow = true;
            }

            if (!includeRow) {
                continue;
            }

            ambulatoryVitalSignToPlotData["csn"].push(row[1]);
            ambulatoryVitalSignToPlotData["numeric_time"].push(Math.round(new Date(row[8]).getTime() / 1000));
            ambulatoryVitalSignToPlotData["encounter_types"].push(row[9]);

            vitalSignToPlotData["csn"].push(row[1]);
            vitalSignToPlotData["numeric_time"].push(Math.round(new Date(row[8]).getTime() / 1000));
            vitalSignToPlotData["encounter_types"].push(row[9]);

            if (row[9] && row[9].includes("Ambulatory")) {
                ambulatoryVitalSignToPlotData["hr"].push(row[2] === null || isNaN(row[2]) ? null : parseFloat(row[2]));
                ambulatoryVitalSignToPlotData["rr"].push(row[3] === null || isNaN(row[3]) ? null : parseFloat(row[3]));
                ambulatoryVitalSignToPlotData["spo2"].push(row[4] === null || isNaN(row[4]) ? null : parseFloat(row[4]));
                ambulatoryVitalSignToPlotData["nbps"].push(row[5] === null || isNaN(row[5]) ? null : parseFloat(row[5]));
                ambulatoryVitalSignToPlotData["nbpd"].push(row[6] === null || isNaN(row[6]) ? null : parseFloat(row[6]));
                ambulatoryVitalSignToPlotData["temp"].push(row[7] === null || isNaN(row[7]) ? null : parseFloat(row[7]));

                vitalSignToPlotData["hr"].push(null);
                vitalSignToPlotData["rr"].push(null);
                vitalSignToPlotData["spo2"].push(null);
                vitalSignToPlotData["nbps"].push(null);
                vitalSignToPlotData["nbpd"].push(null);
                vitalSignToPlotData["temp"].push(null);
            } else {
                ambulatoryVitalSignToPlotData["hr"].push(null);
                ambulatoryVitalSignToPlotData["rr"].push(null);
                ambulatoryVitalSignToPlotData["spo2"].push(null);
                ambulatoryVitalSignToPlotData["nbps"].push(null);
                ambulatoryVitalSignToPlotData["nbpd"].push(null);
                ambulatoryVitalSignToPlotData["temp"].push(null);

                vitalSignToPlotData["hr"].push(row[2] === null || isNaN(row[2]) ? null : parseFloat(row[2]));
                vitalSignToPlotData["rr"].push(row[3] === null || isNaN(row[3]) ? null : parseFloat(row[3]));
                vitalSignToPlotData["spo2"].push(row[4] === null || isNaN(row[4]) ? null : parseFloat(row[4]));
                vitalSignToPlotData["nbps"].push(row[5] === null || isNaN(row[5]) ? null : parseFloat(row[5]));
                vitalSignToPlotData["nbpd"].push(row[6] === null || isNaN(row[6]) ? null : parseFloat(row[6]));
                vitalSignToPlotData["temp"].push(row[7] === null || isNaN(row[7]) ? null : parseFloat(row[7]));
            }
        }

        return [vitalSignToPlotData, ambulatoryVitalSignToPlotData];
    }

    const handleHighlightedPatientVisit = (patientVisit: PatientVisit) => {
        // @ts-ignore
        document.getElementById(`csn-${patientVisit.csn}`)?.scrollIntoView();
        setHighlightedPatientVisit(patientVisit);
    }

    useEffect(() => {
        setMultiVisitOpts({
            "HR": getVisitOpts(props.width, "HR", false, visitSummaryPlotData, cursPos, setCursPos, relatedPatientVisits, handleHighlightedPatientVisit),
            "RR": getVisitOpts(props.width, "RR", false, visitSummaryPlotData, cursPos, setCursPos, relatedPatientVisits, handleHighlightedPatientVisit),
            "SpO2": getVisitOpts(props.width, "SpO2", false, visitSummaryPlotData, cursPos, setCursPos, relatedPatientVisits, handleHighlightedPatientVisit),
            "NBP": getVisitOpts(props.width, "NBP", false, visitSummaryPlotData, cursPos, setCursPos, relatedPatientVisits, handleHighlightedPatientVisit),
            "Temp": getVisitOpts(props.width, "Temp", true, visitSummaryPlotData, cursPos, setCursPos, relatedPatientVisits, handleHighlightedPatientVisit),
        });
        setVisitSummaryOpts(getVisitSummaryOpts(props.width, setCursPos, relatedPatientVisits, handleHighlightedPatientVisit))
    }, [props.width]);

    useEffect(() => {
        setMultiVisitOpts({
            "HR": getVisitOpts(props.width, "HR", false, visitSummaryPlotData, cursPos, setCursPos, relatedPatientVisits, handleHighlightedPatientVisit),
            "RR": getVisitOpts(props.width, "RR", false, visitSummaryPlotData, cursPos, setCursPos, relatedPatientVisits, handleHighlightedPatientVisit),
            "SpO2": getVisitOpts(props.width, "SpO2", false, visitSummaryPlotData, cursPos, setCursPos, relatedPatientVisits, handleHighlightedPatientVisit),
            "NBP": getVisitOpts(props.width, "NBP", false, visitSummaryPlotData, cursPos, setCursPos, relatedPatientVisits, handleHighlightedPatientVisit),
            "Temp": getVisitOpts(props.width, "Temp", true, visitSummaryPlotData, cursPos, setCursPos, relatedPatientVisits, handleHighlightedPatientVisit),
        });
        setVisitSummaryOpts(getVisitSummaryOpts(props.width, setCursPos, relatedPatientVisits, handleHighlightedPatientVisit));
    }, [vitalSignToPlotData, visitSummaryPlotData])

    const refreshData = () => {
        callQuery({
            mrn: props.patientVisit.mrn,
            operation: PATIENT_VISITS_BY_MRN_WITH_SUMMARY,
        }).then((patients: (PatientVisit)[]) => {
            console.log("callSearchPatients returned for internalCSN: " + props.patientVisit.internal_csn)
            const visitSummaryPlotData: any = [
                [], // time
                [], // ED
                [], // Hospital Encounter
                [], // outpatient
                [], // ambulatory
                [], // other
            ];
            
            const relatedPatientVisits = [];
            patients.sort((a, b) => new Date(a.arrival_time).getTime() - new Date(b.arrival_time).getTime());
            for (let pt of patients) {
                let addedToRelated = false;
                if (!pt.encounter_types || pt.encounter_types.length === 0 || (pt.encounter_types?.includes("Emergency") && filterOptions["encounter"].includes("Emergency"))) {
                    visitSummaryPlotData[0].push(Math.round(new Date(pt.arrival_time).getTime() / 1000))
                    visitSummaryPlotData[1].push(5)
                    visitSummaryPlotData[2].push(null)
                    visitSummaryPlotData[3].push(null)
                    visitSummaryPlotData[4].push(null)
                    visitSummaryPlotData[5].push(null)
                    if (!addedToRelated) {
                        relatedPatientVisits.push(pt);
                        addedToRelated = true;
                    }
                } else if (pt.encounter_types?.includes("Hospital Encounter") && filterOptions["encounter"].includes("Hospital Encounter")) {
                    visitSummaryPlotData[0].push(Math.round(new Date(pt.arrival_time).getTime() / 1000))
                    visitSummaryPlotData[1].push(null)
                    visitSummaryPlotData[2].push(4)
                    visitSummaryPlotData[3].push(null)
                    visitSummaryPlotData[4].push(null)
                    visitSummaryPlotData[5].push(null)
                    if (!addedToRelated) {
                        relatedPatientVisits.push(pt);
                        addedToRelated = true;
                    }
                } else if (pt.encounter_types?.includes("Outpatient") && filterOptions["encounter"].includes("Outpatient")) {
                    visitSummaryPlotData[0].push(Math.round(new Date(pt.arrival_time).getTime() / 1000))
                    visitSummaryPlotData[1].push(null)
                    visitSummaryPlotData[2].push(null)
                    visitSummaryPlotData[3].push(3)
                    visitSummaryPlotData[4].push(null)
                    visitSummaryPlotData[5].push(null)
                    if (!addedToRelated) {
                        relatedPatientVisits.push(pt);
                        addedToRelated = true;
                    }
                } else if (pt.encounter_types?.includes("Ambulatory") && filterOptions["encounter"].includes("Ambulatory")) {
                    visitSummaryPlotData[0].push(Math.round(new Date(pt.arrival_time).getTime() / 1000))
                    visitSummaryPlotData[1].push(null)
                    visitSummaryPlotData[2].push(null)
                    visitSummaryPlotData[3].push(null)
                    visitSummaryPlotData[4].push(2)
                    visitSummaryPlotData[5].push(null)
                    if (!addedToRelated) {
                        relatedPatientVisits.push(pt);
                        addedToRelated = true;
                    }
                } else if ((!pt.encounter_types?.includes("Emergency")) && (!pt.encounter_types?.includes("Hospital Encounter")) && (!pt.encounter_types?.includes("Outpatient")) && (!pt.encounter_types?.includes("Ambulatory")) && (filterOptions["encounter"].includes("Other"))) {
                    visitSummaryPlotData[0].push(Math.round(new Date(pt.arrival_time).getTime() / 1000))
                    visitSummaryPlotData[1].push(null)
                    visitSummaryPlotData[2].push(null)
                    visitSummaryPlotData[3].push(null)
                    visitSummaryPlotData[4].push(null)
                    visitSummaryPlotData[5].push(1)
                    if (!addedToRelated) {
                        relatedPatientVisits.push(pt);
                        addedToRelated = true;
                    }
                }
            }
            setRelatedPatientVisits([...relatedPatientVisits]);
            setVisitSummaryPlotData(visitSummaryPlotData)

            if (visitSummaryPlotData.length > 0) {
                getNumericsData(relatedPatientVisits, visitSummaryPlotData[0][0]);
            }

            setIsLoading(false);
        }).catch((err) => {
            setIsLoading(false);
        })

        callQuery({
            mrn: props.patientVisit.mrn,
            operation: DIAGNOSES_BY_MRN,
        }).then((diagnoses: Diagnosis[]) => {
            diagnoses.sort((a, b) => {
                if (a.noted_date !== b.noted_date) {
                    return a.noted_date - b.noted_date;
                }
                return a.desc10.localeCompare(b.desc10);
            });
            setDiagnoses(diagnoses);
        });
    }

    const prepend = (arr: any, val: any) => {
        return [val, ...arr]
    }

    const getNumericsData = (relatedPatientVisits: PatientVisit[], earliestTime: number) => {
        callQuery({
            operation: CHARTED_VITAL_SIGNS_BY_MRN,
            mrn: props.patientVisit.mrn,
            type: "last",
        }).then((vitalSigns: any) => {
            let data = convertToPlotData(relatedPatientVisits, vitalSigns);
            let plotData = data[0]
            let ambulatoryData = data[1]
            setVitalSignToPlotData({
                "HR": [prepend(plotData["numeric_time"], earliestTime), prepend(plotData["hr"], null), prepend(ambulatoryData["hr"], null)],
                "RR": [prepend(plotData["numeric_time"], earliestTime), prepend(plotData["rr"], null), prepend(ambulatoryData["rr"], null)],
                "SpO2": [prepend(plotData["numeric_time"], earliestTime), prepend(plotData["spo2"], null), prepend(ambulatoryData["spo2"], null)],
                "NBP": [prepend(plotData["numeric_time"], earliestTime), prepend(plotData["nbps"], null), prepend(plotData["nbpd"], null), prepend(ambulatoryData["nbps"], null), prepend(ambulatoryData["nbpd"], null)],
                "Temp": [prepend(plotData["numeric_time"], earliestTime), prepend(plotData["temp"], null), prepend(ambulatoryData["temp"], null)],
            });
        });
    }

    useEffect(() => {
        if (props.showPatientVisitSelectorModal && props.patientVisit) {
            setIsLoading(true);

            refreshData();

        }
    }, [props.showPatientVisitSelectorModal]);

    const getSortedListByPatientVisit = () => {
        const list = [...relatedPatientVisits];
        list.sort((a, b) => {
            if (a.arrival_time < b.arrival_time) {
                return -1;
            } else {
                return 1;
            }
        });
        return list;
    }

    const formatDiagnosisDateBadge = (noted_date: number, arrival_time: string) => {
        if (new Date(noted_date).toDateString() === new Date(arrival_time).toDateString()) {
            return (
                <Badge bg="primary">During Visit</Badge>
            )
        } else if (new Date(noted_date) < new Date(arrival_time)) {
            return (
                <Badge bg="secondary">Before Visit</Badge>
            )
        } else {
            return (
                <Badge bg="dark">After Visit</Badge>
            )
        }
    }

    const dispatchOnElement = (elementId: string, event: any) => {
        let overlays = document.getElementById(elementId)?.getElementsByClassName("u-over");
        if (overlays && overlays.length > 0) {
            if (overlays.item && overlays.item(0) !== null) {
                overlays.item(0)!.dispatchEvent(event);
            }
        }
    }

    const doubleClickOnTimeline = () => {
        var event = new MouseEvent('dblclick', {
            'view': window,
            'bubbles': true,
            'cancelable': true
        });
        dispatchOnElement("multivisit-summary", event);
    }

    const escFunction = useCallback((event: any) => {
        if (event.key === "Escape") {
            // @ts-ignore
            if (document.getElementById("multivisit-summary") && document.getElementById("multivisit-summary").getElementsByClassName("u-select") && document.getElementById("multivisit-summary").getElementsByClassName("u-select")[0].clientWidth > 0) {
                // The plots are zoomed in, so Esc key resets zoom
                doubleClickOnTimeline();
                event.stopPropagation();
            }
        }

    }, []);

    useEffect(() => {
        document.addEventListener("keydown", escFunction, false);
        return () => {
            document.removeEventListener("keydown", escFunction, false);
        };
    }, [escFunction]);

    const updateFilterOptions = (option: string, val: any) => {
        setFilterOptions({
            ...filterOptions,
            [option]: val,
        });
    }

    useEffect(() => {
        if (props.showPatientVisitSelectorModal && props.patientVisit) {
            refreshData();
        }
    }, [filterOptions]);

    return (
        <Modal dialogClassName="modal-90w" show={props.showPatientVisitSelectorModal} onHide={() => props.setShowPatientVisitSelectorModal(false)}>
            <Modal.Header closeButton>
                <Modal.Title>Patient Visits for MRN <PersonalIdentifier identifier={props.patientVisit.mrn} /> ({props.patientVisit.age} {props.patientVisit.gender})</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Row>
                    <Col md={6}>
                        <div>
                            <Button variant="secondary" size="sm" onClick={() => setFilterShow(true)}>
                                {(filterOptions["encounter"].length === ENCOUNTER_TYPES.length) ? (
                                    <>All Encounters</>
                                ) : <>{filterOptions["encounter"].join(", ")}</>}{' '}<FontAwesomeIcon icon={faCaretDown} />
                            </Button>
                            {filterShow && (
                                <MultiSelectMenu
                                    selectedItems={filterOptions["encounter"]}
                                    option="encounter"
                                    updateFilterOptions={updateFilterOptions}
                                    availableItems={ENCOUNTER_TYPES}
                                    setShow={setFilterShow}
                                    additionalClassNames="patient-visit-selector-multi-select"
                                />
                            )}
                            <Button className="reset-zoom-btn patient-visit-selector-reset" size="sm" variant="outline-secondary" onClick={doubleClickOnTimeline}>Reset Zoom</Button>
                            {/* <Button size="sm" variant="info" style={{color: 'white', marginLeft: '4px'}} onClick={() => {setShowAssistant(!showAssistant)}}><FontAwesomeIcon icon={faBolt} />{' '}Assistant</Button> */}
                            {/* <Button className="zoom-btn" size="sm" variant="outline-secondary" onClick={() => zoom(true)}><FontAwesomeIcon icon={faPlus} /></Button>
                            <Button className="zoom-btn" size="sm" variant="outline-secondary" onClick={() => zoom(false)}><FontAwesomeIcon icon={faMinus} /></Button> */}
                        </div>
                    </Col>
                    <Col md={6}>
                        <div className="text-right float-end plot-indicator-container">
                            <div className='plot-indicator-container-item'>
                                <span className="plot-indicator-container-icon visit-selector-red"><FontAwesomeIcon fontWeight={800} icon={faCircle} /></span>{' '}<span className="plot-indicator-container-desc">Emergency Department</span>
                                <span className="plot-indicator-container-icon visit-selector-orange"><FontAwesomeIcon fontWeight={800} icon={faCircle} /></span>{' '}<span className="plot-indicator-container-desc">Hospital Encounter</span>
                                <span className="plot-indicator-container-icon visit-selector-green"><FontAwesomeIcon fontWeight={800} icon={faCircle} /></span>{' '}<span className="plot-indicator-container-desc">Outpatient</span>
                                <span className="plot-indicator-container-icon visit-selector-purple"><FontAwesomeIcon fontWeight={800} icon={faCircle} /></span>{' '}<span className="plot-indicator-container-desc">Ambulatory</span>
                                <span className="plot-indicator-container-icon visit-selector-gray"><FontAwesomeIcon fontWeight={800} icon={faCircle} /></span>{' '}<span className="plot-indicator-container-desc">Other</span>
                            </div>
                        </div>
                    </Col>
                </Row>
                {
                    showAssistant && (
                        <VisitSelectorAssistant ws={props.ws} mrn={props.patientVisit.mrn} csn={props.patientVisit.csn} encounterTypes={filterOptions["encounter"]} />
                    )
                }
                <div style={{clear: "both"}}>
                    {
                        visitSummaryOpts && (
                            <UplotReact
                                options={visitSummaryOpts}
                                data={visitSummaryPlotData as uPlot.AlignedData}
                                resetScales={false}
                            />
                        )
                    }
                    {
                        ["HR", "RR", "SpO2", "NBP", "Temp"].map((vs: string) => (
                            <>
                                {
                                    // Display only if there's been more than one vital sign to plot
                                    vitalSignToPlotData[vs] && vitalSignToPlotData[vs][0].length > 1 && (
                                        <UplotReact
                                            key={`uplot-multi-${vs}`}
                                            options={multiVisitOpts[vs]}
                                            data={vitalSignToPlotData[vs] as uPlot.AlignedData}
                                            resetScales={false}
                                        />
                                    )
                                }
                            </>
                        ))
                    }
                </div>
                <ListGroup className="visit-selector-group mt-4">
                    {
                        isLoading ? (
                            <div className="text-center mb-4">
                                <FontAwesomeIcon icon={faSpinner} spin />{' '}Loading...
                            </div>
                        ) : (
                            <>
                                {getSortedListByPatientVisit().filter((pt: any) => !pt.encounter_types?.includes("Ambulatory")).map((visit: PatientVisit & PatientSummary) => {
                                    return (
                                        <PatientVisitSelectorItem 
                                            user={props.user}
                                            visit={visit}
                                            diagnoses={diagnoses}
                                            patientVisit={props.patientVisit}
                                            setShowPatientVisitSelectorModal={props.setShowPatientVisitSelectorModal}
                                            highlightedPatientVisit={highlightedPatientVisit}
                                        />
                                    )
                                })}
                            </>
                        )
                    }
                </ListGroup>
            </Modal.Body>
        </Modal>
    );
});
